/**
 * Links públicos de marcas y campañas.
 *
 * Dominio de marcas: `brands.mx`.
 *   - Portal de la marca:   https://{marca}.brands.mx
 *   - Campaña:              https://{marca}.brands.mx/{campaña}
 *   - Respaldo por ruta:    https://entregas.brands.mx/{marca}[/{campaña}]  (sin DNS comodín)
 * El panel admin vive en `entregas.brands.mx`.
 */

/** Dominio cuyos subdominios son portales de marca. */
export const BRAND_DOMAIN = 'brands.mx';
/** Dominios reconocidos como de marcas. */
// `localhost` permite probar portales en desarrollo: cat.localhost:5173 (Chrome lo resuelve local).
export const BRAND_DOMAINS = ['brands.mx', 'localhost'] as const;
/** Origen del panel y del respaldo por ruta. */
export const APP_ORIGIN = 'https://entregas.brands.mx';

/** Subdominios que no pueden ser marcas (los usa la agencia o el sistema). */
export const RESERVED_SUBDOMAINS = new Set([
  'brands',
  'entregas',
  'escaparate',
  'www',
  'app',
  'admin',
  'api',
  'mail',
  'email',
  'ftp',
  'cdn',
  'static',
  'dev',
  'test',
  'staging',
]);

/** Primeros segmentos de ruta que usa la app y no pueden ser slugs. */
export const RESERVED_SLUGS = new Set([
  'admin',
  'entrar',
  'salir',
  'campanas',
  'c',
  'm',
  'diseno',
  'api',
  'assets',
  'www',
  'app',
  'cuenta',
  '__',
]);

const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,48}[a-z0-9])?$/;

export function slugError(slug: string): string | null {
  if (!slug) return 'Escribe un texto para el link.';
  if (!SLUG.test(slug)) {
    return 'Usa solo minúsculas, números y guiones (sin espacios ni acentos), de 1 a 50 caracteres.';
  }
  if (slug.includes('--')) return 'No uses dos guiones seguidos.';
  if (RESERVED_SLUGS.has(slug)) return `“${slug}” está reservado por el sistema.`;
  return null;
}

/** Validación del subdominio de una marca (más estricta: también excluye subdominios del sistema). */
export function brandSlugError(slug: string): string | null {
  const base = slugError(slug);
  if (base) return base;
  if (RESERVED_SUBDOMAINS.has(slug)) return `“${slug}” no puede usarse como subdominio.`;
  if (slug.length > 40) return 'El subdominio debe tener máximo 40 caracteres.';
  return null;
}

export const isValidSlug = (slug: string): boolean => slugError(slug) === null;

/** Clave única de un link de campaña (documento `campaignSlugs/{clave}`). */
export const linkKey = (clientSlug: string, slug: string): string => `${clientSlug}~${slug}`;

export type BrandRoute = {
  brandSlug: string;
  campaignSlug: string | null;
  host: 'brand' | 'app';
} | null;

/** Si el host es el subdominio de una marca, devuelve su slug. */
export function brandFromHost(host: string): string | null {
  const h = host.toLowerCase().split(':')[0] ?? '';
  for (const d of BRAND_DOMAINS) {
    if (!h.endsWith(`.${d}`)) continue;
    const sub = h.slice(0, -d.length - 1);
    if (sub && !sub.includes('.') && !RESERVED_SUBDOMAINS.has(sub) && isValidSlug(sub)) return sub;
  }
  return null;
}

/**
 * Interpreta host + ruta:
 * - En `{marca}.brands.mx`: `/` → portal; `/{campaña}` → campaña.
 * - En cualquier otro host: `/{marca}` → portal; `/{marca}/{campaña}` → campaña.
 */
export function parseBrandRoute(host: string, pathname: string): BrandRoute {
  const parts = pathname
    .split('/')
    .filter(Boolean)
    .map((p) => decodeURIComponent(p).toLowerCase());
  const sub = brandFromHost(host);
  if (sub) {
    if (parts.length === 0) return { brandSlug: sub, campaignSlug: null, host: 'brand' };
    if (parts.length === 1 && parts[0] && isValidSlug(parts[0])) {
      return { brandSlug: sub, campaignSlug: parts[0], host: 'brand' };
    }
    return null;
  }
  const [a, b] = parts;
  if (parts.length === 1 && a && isValidSlug(a))
    return { brandSlug: a, campaignSlug: null, host: 'app' };
  if (parts.length === 2 && a && b && isValidSlug(a) && isValidSlug(b)) {
    return { brandSlug: a, campaignSlug: b, host: 'app' };
  }
  return null;
}

/** Compatibilidad: solo la parte de campaña. */
export function parseCampaignLink(
  host: string,
  pathname: string,
): { clientSlug: string; slug: string } | null {
  const r = parseBrandRoute(host, pathname);
  return r?.campaignSlug ? { clientSlug: r.brandSlug, slug: r.campaignSlug } : null;
}

/** Host del portal de una marca en el dominio de marcas actual. */
export const brandHost = (brandSlug: string): string => `${brandSlug}.${BRAND_DOMAIN}`;

/** URLs para mostrar al admin: subdominio (definitivo) y respaldo por ruta (funciona sin DNS). */
export function brandUrls(brandSlug: string): { subdomain: string; path: string } {
  return { subdomain: `https://${brandHost(brandSlug)}`, path: `${APP_ORIGIN}/${brandSlug}` };
}

export function campaignUrls(
  clientSlug: string,
  slug: string,
): { subdomain: string; path: string } {
  return {
    subdomain: `https://${brandHost(clientSlug)}/${slug}`,
    path: `${APP_ORIGIN}/${clientSlug}/${slug}`,
  };
}

/** Ruta relativa dentro del host actual: en el subdominio de la marca no se repite su slug. */
export function campaignPath(host: string, clientSlug: string, slug: string): string {
  return brandFromHost(host) === clientSlug ? `/${slug}` : `/${clientSlug}/${slug}`;
}

export function brandPath(host: string, brandSlug: string): string {
  return brandFromHost(host) === brandSlug ? '/' : `/${brandSlug}`;
}
